import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Node as PMNode } from '@tiptap/pm/model';

export interface BlockInfo {
  id: string;
  pos: number;
  type: string;
  level?: number;
  element: HTMLElement | null;
}

export interface BlockGutterState {
  blocks: BlockInfo[];
  editorElement: HTMLElement | null;
  activeBlockIndex: number | null;
}

export const blockGutterPluginKey = new PluginKey<BlockGutterState>('blockGutter');

export interface BlockGutterOptions {
  onUpdate?: (state: BlockGutterState) => void;
}

export const BlockGutterExtension = Extension.create<BlockGutterOptions>({
  name: 'blockGutter',

  addOptions() {
    return {
      onUpdate: undefined,
    };
  },

  addProseMirrorPlugins() {
    const options = this.options;

    return [
      new Plugin<BlockGutterState>({
        key: blockGutterPluginKey,

        state: {
          init() {
            return {
              blocks: [],
              editorElement: null,
              activeBlockIndex: null,
            };
          },

          apply(tr, value, oldState, newState) {
            // Recalculate blocks on every transaction
            const blocks: BlockInfo[] = [];
            let blockIndex = 0;
            let offset = 0;

            // Use forEach to iterate only direct children of doc (top-level blocks)
            newState.doc.forEach((node: PMNode) => {
              const blockType = node.type.name;
              const level = blockType === 'heading' ? (node.attrs.level as number) : undefined;

              blocks.push({
                id: `block-${blockIndex++}`,
                pos: offset,
                type: blockType,
                level,
                element: null, // Will be populated by the view
              });

              // Move offset past this node
              offset += node.nodeSize;
            });

            // Compute which block the cursor is in
            const cursorPos = newState.selection.$from.pos;
            let activeBlockIndex: number | null = null;
            for (let i = 0; i < blocks.length; i++) {
              if (blocks[i].pos <= cursorPos) {
                activeBlockIndex = i;
              } else {
                break;
              }
            }

            return {
              blocks,
              editorElement: value.editorElement,
              activeBlockIndex,
            };
          },
        },

        view(editorView) {
          // Update element references after DOM updates
          const updateElements = () => {
            const state = blockGutterPluginKey.getState(editorView.state);
            if (!state) return;

            const editorDom = editorView.dom;

            const updatedBlocks = state.blocks.map((block) => {
              try {
                // Use side=1 to prefer content AFTER the position.
                // At block boundaries (e.g. right after a table), side=0
                // can resolve to the end of the previous block. side=1
                // ensures we get the DOM node for THIS block.
                const { node, offset } = editorView.domAtPos(block.pos, 1);
                if (node === editorDom) {
                  const child = node.childNodes[offset];
                  if (child instanceof HTMLElement) return { ...block, element: child };
                }
                // Position resolved inside a block (text node, nested element).
                // Walk up the DOM tree to find the editor's direct child.
                let el: HTMLElement | null =
                  node instanceof HTMLElement ? node : node.parentElement;
                while (el && el !== editorDom && el.parentElement !== editorDom) {
                  el = el.parentElement;
                }
                if (el && el !== editorDom) return { ...block, element: el };
                return { ...block, element: null };
              } catch {
                return { ...block, element: null };
              }
            });

            // Always fire onUpdate with fresh element references.
            // ProseMirror can rebuild DOM nodes (especially around tables/nodeViews),
            // making previously-cached element references stale/detached.
            // The consumer (handleBlockGutterUpdate) handles deduplication via
            // element reference comparison to prevent unnecessary React re-renders.
            const newState: BlockGutterState = {
              blocks: updatedBlocks,
              editorElement: editorView.dom as HTMLElement,
              activeBlockIndex: state.activeBlockIndex,
            };

            options.onUpdate?.(newState);
          };

          // Initial update
          setTimeout(updateElements, 0);

          return {
            update() {
              setTimeout(updateElements, 0);
            },
            destroy() {
              // Cleanup
            },
          };
        },
      }),
    ];
  },
});

// Helper to get block label
export function getBlockLabel(type: string, level?: number): string {
  if (type === 'heading' && level) {
    return `H${level}`;
  }

  const labels: Record<string, string> = {
    paragraph: 'p',
    bulletList: 'ul',
    orderedList: 'ol',
    listItem: 'li',
    blockquote: 'quote',
    codeBlock: 'code',
    horizontalRule: 'hr',
    table: 'table',
    image: 'img',
    callout: 'callout',
    faqSchema: 'faq',
    statsGrid: 'stats',
    ctaBlock: 'cta',
  };

  return labels[type] || type.slice(0, 3);
}

// Helper to get keyboard shortcut for block type
export function getBlockShortcut(type: string, level?: number): string | null {
  if (type === 'heading' && level) {
    return `Ctrl+Alt+${level}`;
  }

  const shortcuts: Record<string, string> = {
    paragraph: 'Ctrl+Alt+0',
    bulletList: 'Ctrl+Shift+8',
    orderedList: 'Ctrl+Shift+7',
    blockquote: 'Ctrl+Shift+B',
    codeBlock: 'Ctrl+Alt+C',
  };

  return shortcuts[type] || null;
}

export default BlockGutterExtension;
